import React from 'react';
import { FiLoader } from 'react-icons/fi';

const LoadingSpinner = ({ 
  size = 'medium', 
  message = 'Loading...', 
  overlay = false,
  className = '',
  inline = false 
}) => {
  const sizeClasses = {
    small: 'loading-spinner-small',
    medium: 'loading-spinner-medium',
    large: 'loading-spinner-large'
  };

  const spinnerClass = `loading-spinner ${sizeClasses[size]} ${inline ? 'inline' : ''} ${className}`;
  
  const content = (
    <div className={spinnerClass} role="status" aria-label={message}>
      <div className="spinner-ring">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
      {message && <p className="loading-message">{message}</p>}
    </div>
  );

  if (overlay) {
    return (
      <div className="loading-overlay" role="dialog" aria-modal="true">
        {content}
      </div>
    );
  }

  return content;
};

// Enhanced button loading state
export const LoadingButton = ({ 
  children, 
  loading = false, 
  disabled = false, 
  onClick, 
  className = '',
  loadingText = "Loading...",
  ...props 
}) => {
  return (
    <button 
      className={`loading-button ${className} ${loading ? 'loading' : ''}`}
      disabled={disabled || loading}
      onClick={onClick}
      aria-disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <FiLoader 
          size={16} 
          style={{ marginRight: '8px' }} 
          className="spinner-icon"
        />
      )}
      <span>
        {loading ? loadingText : children}
      </span>
    </button>
  );
};

// Skeleton loading component for content placeholders
export const SkeletonLoader = ({ width = '100%', height = '20px', className = '' }) => {
  return (
    <div 
      className={`skeleton ${className}`}
      style={{ width, height, borderRadius: '4px' }}
      aria-hidden="true"
    />
  );
};

export default LoadingSpinner;

// Add CSS for new components - injected globally
const globalStyles = `
  .spinner-icon {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  .loading-button {
    position: relative;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  
  .loading-button.loading {
    pointer-events: none;
  }
  
  .loading-button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  .loading-spinner.inline {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  
  .skeleton {
    background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
  }
  
  @keyframes loading {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

// Inject styles only once
if (typeof document !== 'undefined' && !document.getElementById('loading-spinner-styles')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'loading-spinner-styles';
  styleElement.innerHTML = globalStyles;
  document.head.appendChild(styleElement);
}